import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Scale, Hammer, Coins, CheckCircle2 } from "lucide-react";
import MakingChargeCalculator from "@/components/MakingChargeCalculator";

export default function MakingCharges() {
    return (
        <div>
            <section className="relative h-[52vh] overflow-hidden">
                <img src="https://images.unsplash.com/photo-1611085583191-a3b181a88401?w=1800" alt="Making charges" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[color:var(--charcoal)]/60 flex items-center">
                    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 w-full">
                        <div className="eyebrow text-[color:var(--champagne-light)]">Honest Pricing</div>
                        <h1 className="font-serif-luxe text-5xl md:text-7xl text-white mt-3 leading-tight" data-testid="mc-title">Why Making Charges Matter</h1>
                        <p className="font-serif-luxe italic text-white/85 text-xl md:text-2xl mt-4 max-w-2xl">
                            Every rupee you invest deserves a clear explanation.
                        </p>
                    </div>
                </div>
            </section>

            <section className="max-w-3xl mx-auto px-6 py-16">
                <div className="prose max-w-none">
                    <p className="text-lg font-serif-luxe italic leading-relaxed text-[color:var(--charcoal)]">
                        Buying jewellery is one of the most meaningful investments a family makes. At Singh Jewellers, we believe you should know exactly what you are paying for — the pure gold, and the craft that shaped it.
                    </p>

                    <div className="divider-line my-8" />

                    <h2 className="font-serif-luxe text-3xl mt-8 mb-4 text-[color:var(--charcoal)]">What is a making charge?</h2>
                    <p className="text-[color:var(--muted-brown)] leading-relaxed mb-6">
                        A making charge is the cost of turning raw gold into a finished piece of jewellery — the hours of hand-work, the skill of the karigar, the finish, the polish, the setting. It is charged over and above the pure gold value.
                    </p>

                    <h2 className="font-serif-luxe text-3xl mt-8 mb-4 text-[color:var(--charcoal)]">How is it calculated?</h2>
                    <p className="text-[color:var(--muted-brown)] leading-relaxed mb-4">Making charges are usually expressed in one of two ways:</p>
                    <ul className="space-y-3 mb-8 text-[color:var(--muted-brown)]">
                        <li className="flex gap-3"><CheckCircle2 size={18} className="text-[color:var(--champagne)] flex-shrink-0 mt-0.5" /><span><strong className="text-[color:var(--charcoal)]">As a percentage</strong> — a % of the gold value (e.g. 8% of ₹1,00,000 = ₹8,000).</span></li>
                        <li className="flex gap-3"><CheckCircle2 size={18} className="text-[color:var(--champagne)] flex-shrink-0 mt-0.5" /><span><strong className="text-[color:var(--charcoal)]">Per gram</strong> — a fixed rupee amount per gram of jewellery.</span></li>
                    </ul>

                    <h2 className="font-serif-luxe text-3xl mt-8 mb-4 text-[color:var(--charcoal)]">Why they vary from piece to piece</h2>
                    <p className="text-[color:var(--muted-brown)] leading-relaxed mb-4">Making charges depend on the design complexity, finish and technique used:</p>
                    <ul className="space-y-3 mb-8 text-[color:var(--muted-brown)]">
                        <li className="flex gap-3"><CheckCircle2 size={18} className="text-[color:var(--champagne)] flex-shrink-0 mt-0.5" /><span>Plain gold bands and simple chains carry lower making charges.</span></li>
                        <li className="flex gap-3"><CheckCircle2 size={18} className="text-[color:var(--champagne)] flex-shrink-0 mt-0.5" /><span>Handcrafted heritage pieces — Chandrahaar, Guluband, Tehri Nath — carry higher charges due to the intricate work involved.</span></li>
                        <li className="flex gap-3"><CheckCircle2 size={18} className="text-[color:var(--champagne)] flex-shrink-0 mt-0.5" /><span>Diamond and stone-set jewellery includes setting labour, in addition to the stone cost.</span></li>
                    </ul>

                    <div className="grid md:grid-cols-3 gap-4 my-10">
                        <StatCard icon={ShieldCheck} title="Transparency" text="Every quote we give is broken down — gold value, making charge, GST." />
                        <StatCard icon={Scale} title="Fair Weight" text="We weigh every piece in front of you. What you see is what you pay for." />
                        <StatCard icon={Hammer} title="Honest Craft" text="Our karigars are compensated fairly, so the craft continues for generations." />
                    </div>

                    <h2 className="font-serif-luxe text-3xl mt-8 mb-4 text-[color:var(--charcoal)]">The Singh Jewellers promise</h2>
                    <p className="text-[color:var(--muted-brown)] leading-relaxed mb-8">
                        We keep our making charges competitive and transparent. We do not inflate weights, we do not add hidden margins, and we always explain the numbers before you decide. If you find a comparable piece elsewhere at a lower total price, come talk to us — we will look at it honestly with you.
                    </p>
                </div>
            </section>

            <section className="bg-[color:var(--beige)]/50 py-16">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-center mb-8">
                        <div className="eyebrow">Try It Yourself</div>
                        <h2 className="font-serif-luxe text-4xl mt-3">See the honest price before you visit</h2>
                    </div>
                    <MakingChargeCalculator />
                </div>
            </section>

            <section className="max-w-3xl mx-auto px-6 py-16 text-center">
                <Coins className="mx-auto text-[color:var(--champagne)] mb-4" size={30} />
                <h3 className="font-serif-luxe text-3xl mb-4">Still have questions?</h3>
                <p className="text-[color:var(--muted-brown)] mb-8">
                    Every family is different, every occasion unique. Talk to our team — we will walk you through the numbers, piece by piece.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                    <Link to="/contact" className="btn-primary">Book a Consultation</Link>
                    <Link to="/gold-rates" className="btn-outline">View Today's Rates</Link>
                </div>
            </section>
        </div>
    );
}

function StatCard({ icon: Icon, title, text }) {
    return (
        <div className="card-luxe p-5 text-center">
            <Icon className="mx-auto text-[color:var(--champagne)] mb-3" size={22} />
            <div className="font-serif-luxe text-lg mb-2">{title}</div>
            <div className="text-xs text-[color:var(--muted-brown)] leading-relaxed">{text}</div>
        </div>
    );
}
